import React, { useCallback, useRef } from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../../../i18n/constants';
import { getUserLanguage } from '../../../i18n/helpers';
import type { Category } from '../../../stores/categories/CategoriesStore';
import { useStores } from '../../../stores/useStores';
import Chevron from '../../SVGIcons/Chevron/Chevron';
import carouselStyles from '../GamesCarousel.module.scss';
import { seenGamesLobbyListener } from '../seenGamesImpression';

import { NumberedGameItem } from './NumberedGameItem';
import styles from './NumberedGamesCarousel.module.scss';

export const NumberedGamesCarousel = observer(function NumberedGamesCarousel({
  id,
  carouselClassName,
  carouselLeftArrowClassName,
  carouselRightArrowClassName,
}: {
  id: Category['masterGameSubCategoryId'];
  carouselClassName?: string;
  carouselLeftArrowClassName?: string;
  carouselRightArrowClassName?: string;
}) {
  const { i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);
  const { categoriesStore } = useStores();
  const category = categoriesStore.categoriesRegistry[id];

  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollCarousel = useCallback((scrollOffset: number) => {
    const $el = carouselRef.current;
    if (!$el) return;
    $el.scrollTo({ left: $el.scrollLeft + scrollOffset, behavior: 'smooth' });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(
    throttle((ev: React.UIEvent<HTMLDivElement>) => {
      seenGamesLobbyListener();
      const $el = ev.target as HTMLDivElement;
      const { scrollLeft, scrollWidth, clientWidth } = $el;
      const $wrapper = $el.parentNode as HTMLDivElement;
      if (scrollLeft < 10) {
        $wrapper.classList.add(carouselStyles.isStart);
      } else {
        $wrapper.classList.remove(carouselStyles.isStart);
      }
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        $wrapper.classList.add(carouselStyles.isEnd);
      } else {
        $wrapper.classList.remove(carouselStyles.isEnd);
      }
    }, 100),
    [],
  );

  if (!category) return null;

  const rowName = category.name[language] || category.name[DEFAULT_LANGUAGE];
  const games = category.subCategoryGames;

  return (
    <div className={classNames(carouselStyles.wrapper, carouselClassName, { [carouselStyles.isStart]: true })}>
      {games.length > 6 && (
        <button
          className={classNames(carouselStyles.arrow, carouselStyles.arrowLeft, carouselLeftArrowClassName)}
          onClick={() => scrollCarousel(-300)}
        >
          <Chevron direction="left" />
        </button>
      )}
      <div ref={carouselRef} className={styles.carousel} onScroll={onScroll}>
        {games.map((game, idx) => (
          <NumberedGameItem key={game.masterCasinoGameId} game={game} rank={idx} rowName={rowName} />
        ))}
      </div>
      {games.length > 6 && (
        <button
          className={classNames(carouselStyles.arrow, carouselStyles.arrowRight, carouselRightArrowClassName)}
          onClick={() => scrollCarousel(300)}
        >
          <Chevron direction="right" />
        </button>
      )}
    </div>
  );
});
